import { formatCurrency } from '../../utils/formatting';

// Block L follow-up 9a (Carla, 2026-10-12): a seat change says the seat count change AND the cost
// change. Decision 3 (round 3, 2026-10-12): seats follow the member count, so nobody changes them by
// hand any more: the confirmation dialog and the + and − that opened it are gone. What remains is
// the wording for the changes that DO happen: a member leaving (the removal dialog) and a member
// joining (the add form's note), and the read-only seat cost in Settings > Team.
//
// THE PRICE IS THE ONE STRIPE CHARGES. `priceInfo` comes from GET /api/team/seat-info, which reads
// the Team subscription item's own Stripe price (lib/teamBilling getTeamSeatInfo), and a seat change
// sets that same item's quantity (syncTeamSeatsToStripe). Nothing here holds a
// price of its own. When that price has no flat per-seat amount the server sends priceInfo null, and
// the cost is said to be unknown.
//
// WHAT HAPPENS TO THIS PERIOD follows prorationBehaviorFor (lib/teamBilling.js): a seat added is
// prorated onto the next bill; a seat removed on a monthly plan stays paid to the end of the period
// with no credit; a seat removed on an annual plan is credited for the unused part.

// The change, worked out from seat-info. Pure, so the sums are tested on their own.
export function seatChange(seatInfo, toSeats) {
  const fromSeats = Number(seatInfo?.paidSeats);
  const annual = seatInfo?.billingCycle === 'annual';
  const unit = seatInfo?.priceInfo && Number.isFinite(Number(seatInfo.priceInfo.amount))
    ? Number(seatInfo.priceInfo.amount) : null;
  const currency = seatInfo?.priceInfo?.currency || null;
  return {
    fromSeats,
    toSeats,
    annual,
    currency,
    unit,
    fromCost: unit == null ? null : unit * fromSeats,
    toCost: unit == null ? null : unit * toSeats,
    delta: unit == null ? null : unit * (toSeats - fromSeats),
    increase: toSeats > fromSeats,
  };
}

// The seats a member's removal leaves: the server sets the Team subscription to its member count,
// never below two (syncTeamSeatsToStripe). null when the removal does not change the seats or there
// is no Team subscription to change.
export function seatsAfterRemoval(seatInfo) {
  if (!seatInfo || seatInfo.teamPlan === false || seatInfo.paidSeats == null) return null;
  const after = Math.max(2, Number(seatInfo.usedSeats) - 1);
  return after === Number(seatInfo.paidSeats) ? null : after;
}

// The seats a member JOINING makes: the same rule. null when it does not change them (a team still
// under the two seat minimum) or there is no Team subscription.
export function seatsAfterJoin(seatInfo) {
  if (!seatInfo || seatInfo.teamPlan === false || seatInfo.paidSeats == null) return null;
  const after = Math.max(2, Number(seatInfo.usedSeats) + 1);
  return after === Number(seatInfo.paidSeats) ? null : after;
}

const per = (annual) => (annual ? 'a year' : 'a month');
const money = (n, currency, lang) => formatCurrency(n, lang, currency || undefined);

export function periodTerms(change) {
  if (change.increase) return 'Stripe adds a part-period charge for the added seat to your next bill.';
  return change.annual
    ? 'Stripe credits the unused part of the year to your account, taken off future bills.'
    : 'The seat stays paid to the end of this billing period; there is no refund for the rest of it.';
}

// One sentence, for a dialog that already has its own text (removing a member).
export function seatChangeSentence(change, lang = 'en-GB') {
  const seats = `Your seats go from ${change.fromSeats} to ${change.toSeats}`;
  if (change.unit == null) return `${seats}. The price per seat could not be read, so the new cost is not shown here.`;
  return `${seats}: ${money(change.fromCost, change.currency, lang)} to ${money(change.toCost, change.currency, lang)} ${per(change.annual)}. ${periodTerms(change)}`;
}

// The read-only cost line for Settings > Team: what the seats cost now, from the same Stripe price.
export function seatCostText(seatInfo, lang = 'en-GB') {
  const c = seatChange(seatInfo, Number(seatInfo?.paidSeats));
  if (c.unit == null) return 'The price per seat could not be read from your subscription, so the cost is not shown here.';
  return `${money(c.fromCost, c.currency, lang)} ${per(c.annual)} (${money(c.unit, c.currency, lang)} per seat).`;
}
